import type { JobsApi } from '@/features/jobs/api/job-api'
import {
  GetJobRatingStatusRequest,
  SubmitPlatformRatingRequest,
  SubmitProRatingRequest,
} from '@taxfyle/api-internal/internal/job_rating_pb'
import { mapToJobRatingStatuses } from '@/features/jobs/details/ratings/ratings-mappers'
import { Id } from '@/infra/id'

export class RatingsStore {
  /**
   * @param _api - JobsApi instance for rating operations
   */
  constructor(private readonly _api: JobsApi) {}

  /**
   * Gets job rating status
   * @param jobId - Job identifier
   * @param signal - Abort signal
   * @returns Job ratings status
   */
  async getRatingsStatus(jobId: string, signal: AbortSignal | undefined) {
    const request = new GetJobRatingStatusRequest().setJobId(jobId)

    const response = await this._api.ratings.getJobRatingStatus(request)
    signal?.throwIfAborted()
    return mapToJobRatingStatuses(response)
  }

  /**
   * Submits platform rating.
   */
  async submitPlatformRating({
    proUserId,
    comment,
    rating,
    jobId,
  }: SubmitPlatformRatingOptions) {
    const request = new SubmitPlatformRatingRequest()
      .setJobId(jobId)
      .setRating(rating)
      .setComment(comment)

    if (Id.some(proUserId)) {
      request.setProviderUserPublicId(proUserId)
    }

    await this._api.ratings.submitPlatformRating(request)
  }

  /**
   * Submits a rating for a professional associated with a job.
   */
  async submitProRating({
    proUserId,
    comment,
    rating,
    jobId,
  }: SubmitProRatingOptions) {
    const request = new SubmitProRatingRequest()
      .setJobId(jobId)
      .setRating(rating)
      .setComment(comment)
      .setProviderUserPublicId(proUserId)

    await this._api.ratings.submitProRating(request)
  }
}

export interface SubmitProRatingOptions {
  jobId: string
  rating: number
  comment: string
  proUserId: string
}

export interface SubmitPlatformRatingOptions {
  jobId: string
  rating: number
  comment: string
  proUserId?: string
}
